import isPlainObject from 'lodash/isPlainObject'

export default class Conversion {
  constructor () {
    // the naming is confusing
    this.installed = false
    this.installFailed = false
    this.setupCompleted = false
    this.installing = false
    // Default behavior: do not push anything
    this.pushWithoutInstall = false
    this.pushWithoutConsent = false // push without consent can be made only when queuing is possible
    this.deferred = []
    // exit current call stack to allow the subclasses to be initialized
    setTimeout(() => {
      this._postConstructor()
    }, 10)
  }

  _postConstructor () {
    // if consent already given no need to listen to anything
    this._checkConsentGiven()
    || document.addEventListener('cookies:accepted:all', () => this._handleConsentGiven())
  }

  // interface
  install () {}
  setup () {}
  handleConsentGiven () {}
  deliver (item) { return item }
  // push (...args) {this.processing(super.push(...args))}
  static formatItems (...items) { return items.map(item => this.formatItem(item)) }
  static formatItem (item) { return item }
  // end interface

  _checkConsentGiven () {
    this.consentGiven && this._handleConsentGiven()
    return this.consentGiven
  }

  async _handleConsentGiven () {
    await this.installMaybe()
    await this.setupMaybe()
    this.handleConsentGiven()
    setTimeout(() => this.processDeferred(), 0)
  }

  async setupMaybe () {
    this.shouldSetup && this.setup()
    this.setupCompleted = true
  }

  async installMaybe () {
    console.debug('installMaybe', 'this.shouldInstall', this.shouldInstall, 'this.canInstall', this.canInstall,
      'this.installed', this.installed, 'this.installFailed', this.installFailed)
    if (this.shouldInstall) {
      if (this.installing) { // just failsafe due to async nature
        setTimeout(() => this._handleConsentGiven(), 200)
        return
      }
      this.installing = true
      try {
        console.debug('installing...')
        await this.install()
        console.debug('installed')
        this.installed = true
        this.installing = false
      } catch (error) {
        console.warn('install failed', error)
        this.installFailed = true
      }
    } else if (!this.installable) {
      this.installed = true
    }
    return this.installed
  }

  // will defer or return args to be pushed
  push (...args) {
    if (this.acceptsPush) {
      const klass = this.constructor
      const items = klass.mapEventNames(...klass.filterEvents(...klass.normalizeArgs(...args)))
      return klass.formatItems(...items).map((item) => {
        console.debug('deliver', item)
        return this.deliver(item)
      })
    }

    this.defer(args)
    return []
  }

  defer (args) {
    console.debug('defer', args)
    this.deferred.push(args)
  }

  processDeferred () {
    console.debug('processDeferred', this.canPush, this.deferred.length)
    if (this.acceptsPush) {
      while (this.deferred.length > 0) {
        this.push(...this.deferred.shift())
      }
    }
  }

  get shouldSetup () {
    return !this.setupCompleted && this.acceptsPush
  }

  get acceptsPush () {
    return this.canPush && this.consentToPush
  }

  get consentToPush () {
    return this.pushWithoutConsent || this.consentGiven
  }

  get canPush () {
    return this.pushWithoutInstall || !this.installable || this.installed
  }

  get shouldInstall () {
    return this.consentGiven && this.canInstall
  }

  get canInstall () {
    return this.installable && !this.installFailed
  }

  get installable () {
    return !this.installed && typeof this.install === 'function'
  }

  get consentGiven () {
    return this.storage?.getItem('cookiesAccepted') === 'true'
  }

  get storage () {
    return window.localStorage
  }

  static normalizeArgs (...input) {
    if (typeof input[0] === 'string') {
      const item = {}
      if (typeof input[1] === 'string') {
        item.event = input[1]
        item.type = input[0]
      } else {
        item.event = input[1]
        if (isPlainObject(input[1])) {
          Object.assign(item, input[1])
        } else {
          item.type = input[0]
        }
      }
      input[2] && Object.assign(item, input[2] || {})
      return [item]
    }
    return input.map((item) => {
      if (item instanceof Array) {
        return this.normalizeArgs(...item)
      } else {
        return item
      }
    })
  }

  static filterEvents (...items) {
    return items.filter(item => this.filterEvent(item))
  }

  static filterEvent ({ event }) {
    if (typeof event !== 'string') return true // bypass not event items
    return (
      (this.expectedEvents.length === 0 || this.expectedEvents.includes(event))
      && (this.ignoredEvents.length === 0 || !this.ignoredEvents.includes(event))
    )
  }

  // Automatically tracked
  static ignoredEvents = []
  // Limit to these events
  static expectedEvents = []

  static mapEventNames (...items) {
    return items.map(({ type, event, ...rest }) => {
      return { type, event: this.eventNamesMap[event] || event, ...rest }
    })
  }

  static eventNamesMap = {}

  static instance ({ ...args } = {}) {
    if (this._instance) return this._instance

    this._instance = new this(args)
    return this._instance
  }
}
